import React, { useEffect, useRef, useState } from 'react';
import { InputNumber, Select } from 'antd';
import { useNavigate } from 'react-router-dom';
import { CloseOutlined } from '@ant-design/icons';
import clsx from 'clsx';
import { IconArrow } from '../../../Common/Icon';
import { useCategory } from '../../../../store/category';
import { useTipContext } from '../TipContextProvider';

import styles from './index.module.scss';
import { useTipStatistic } from '../../Home/HowMuchToTip/CategoryStatistics/CategoryChart/useTipStatistic';

const { Option, OptGroup } = Select;

export default function HowMuchToTip(): React.ReactElement {
  const navigate = useNavigate();
  const [selectedValue, setSelectedValue] = useState<string | undefined>();
  const [selectedIcon, setSelectedIcon] = useState<string | undefined>();
  const [categoryId, setCategoryId] = useState<string | undefined>();
  const [subCategoryId, setSubCategoryId] = useState<string | undefined>();

  const {
    billValue,
    tipValue,
    tipAmount,
    totalAmount,
    setBillValue,
    setTipValue,
  } = useTipContext();
  const { listAll } = useCategory();
  const getCategories = listAll.response?.data;

  const { getTipStatistic, response } = useTipStatistic();
  const tipStatCache = useRef<Record<string, any>>({});

  const [customerAvg, setCustomerAvg] = useState<number>(0);
  const [workerAvg, setWorkerAvg] = useState<number>(0);

  useEffect(() => {
    if (!response || !response.data || !response.subCategoryId) {
      return;
    }
    const { data: statistics, subCategoryId: responseId } = response;

    tipStatCache.current[responseId] = statistics;

    if (subCategoryId === responseId) {
      setCustomerAvg(Math.ceil(statistics.customerAvgPercent ?? 0));
      setWorkerAvg(Math.ceil(statistics.workerAvgPercent ?? 0));
    }
  }, [response, subCategoryId]);

  const handleChange = (value: string) => {
    const selectedCategory = getCategories?.find((cat) =>
      cat.subCategories.some((sub) => sub.header === value)
    );
    if (!selectedCategory) return;

    const selectedSubcategory = selectedCategory.subCategories.find(
      (sub) => sub.header === value
    );
    if (!selectedSubcategory) return;

    const formattedValue = `${selectedCategory.name} / ${selectedSubcategory.header}`;
    const categoryKey = selectedCategory.id;
    const subKey = selectedSubcategory.id;

    setSelectedValue(formattedValue);
    setSelectedIcon(selectedCategory.file?.location);
    setCategoryId(categoryKey);
    setSubCategoryId(subKey);

    if (tipStatCache.current[subKey]) {
      const cached = tipStatCache.current[subKey];
      setCustomerAvg(Math.ceil(cached.customerAvgPercent) ?? 0);
      setWorkerAvg(Math.ceil(cached.workerAvgPercent) ?? 0);
    } else {
      getTipStatistic(subKey);
    }
  };
  const inputRef = useRef<any>(null);
  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <h2>How much to tip?</h2>
        <div className={styles.learnMore}>
          <div
            className={styles.link}
            role="button"
            tabIndex={0}
            onClick={() => {
              if (categoryId && subCategoryId) {
                navigate(`/categories/${categoryId}/${subCategoryId}`);
              } else {
                navigate('/categories');
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (categoryId && subCategoryId) {
                  navigate(`/categories/${categoryId}/${subCategoryId}`);
                } else {
                  navigate('/categories');
                }
              }
            }}
          >
            <IconArrow style={{ color: '#49cf6d' }} />
            <span className={styles.link}>View Industry / Service Page</span>
          </div>
        </div>
      </div>

      <div>
        <h5 style={{ marginBottom: '5px' }}>Industry / Occupation</h5>
        <Select
          className={styles.selectIndustry}
          size="large"
          style={{ height: 50, lineHeight: '50px', color: '#FFFFFF' }}
          dropdownStyle={{ background: '#42454b', color: '#FFFFFF' }}
          onChange={handleChange}
          placeholder="Industry / Occupation"
          value={selectedValue}
          popupClassName="customPopupDropdown"
          labelRender={() => (
            <div className={styles.selectedCategory}>
              {selectedIcon && (
                <img
                  src={selectedIcon}
                  alt="Selected icon"
                  className={styles.icon}
                />
              )}
              {selectedValue}
            </div>
          )}
        >
          {getCategories?.map((cat) => (
            <OptGroup label={cat.name} key={cat.id}>
              {cat.subCategories.map((sub) => (
                <Option value={sub.header} key={sub.id}>
                  <div className={styles.selectedCategory}>
                    {cat.file?.location && (
                      <img
                        src={cat.file.location}
                        alt="Subcategory icon"
                        className={styles.icon}
                      />
                    )}
                    {sub.header}
                  </div>
                </Option>
              ))}
            </OptGroup>
          ))}
        </Select>
      </div>

      <div className={styles.metrics}>
        <div
          className={clsx(styles.avg, {
            [styles.active]: tipValue === customerAvg,
          })}
          role="button"
          tabIndex={-1}
          onClick={() => setTipValue(customerAvg)}
          style={{ cursor: 'pointer' }}
        >
          <h5>Customer Avg:</h5>
          <div className={styles.value}>
            <h4>{customerAvg}%</h4>
          </div>
        </div>

        <div
          className={clsx(styles.avg, {
            [styles.active]: tipValue === workerAvg,
          })}
          role="button"
          tabIndex={-1}
          onClick={() => setTipValue(workerAvg)}
          style={{ cursor: 'pointer' }}
        >
          <h5>Worker Avg:</h5>
          <div className={styles.value}>
            <h4>{workerAvg}%</h4>
          </div>
        </div>
      </div>

      <div className={styles.itemInput}>
        <span className={styles.label}>Bill:</span>
        <InputNumber
          value={billValue}
          onKeyDown={(e) => {
            if (
              !/[0-9.]/.test(e.key) &&
              e.key !== 'Backspace' &&
              e.key !== 'Delete' &&
              e.key !== 'ArrowLeft' &&
              e.key !== 'ArrowRight' &&
              e.key !== 'Tab'
            ) {
              e.preventDefault();
              return;
            }

            if (/[0-9]/.test(e.key)) {
              const selectionStart =
                (e.target as HTMLInputElement).selectionStart ?? 0;
              const selectionEnd =
                (e.target as HTMLInputElement).selectionEnd ?? 0;
              const currentValue = (e.target as HTMLInputElement).value;

              const newValue =
                currentValue.substring(0, selectionStart) +
                e.key +
                currentValue.substring(selectionEnd);

              const numericValue = Number(newValue.replace(/[^\d.]/g, ''));
              if (numericValue > 9000000) {
                e.preventDefault();
              }
            }
          }}
          onChange={(val) => {
            if (val == null) return setBillValue(null);

            if (val === 0 && billValue !== 0) {
              setBillValue(0);
              setTimeout(() => {
                const input = document.querySelector<HTMLInputElement>(
                  '.ant-input-number-input'
                );
                if (input) {
                  const len = input.value.length;
                  input.setSelectionRange(len, len);
                }
              }, 0);
              return;
            }

            setBillValue(val);
          }}
          min={0}
          max={9000000}
          precision={2}
          formatter={(value) =>
            value != null
              ? `$${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              : ''
          }
          parser={(value) => {
            if (!value) return 0;
            const num = value.replace(/[^\d.]/g, '');
            return Number(num);
          }}
          className={styles.input}
          style={{ color: 'white' }}
          controls={false}
        />
        {billValue !== null && (
          <CloseOutlined
            className={styles.icon}
            onClick={() => setBillValue(null)}
          />
        )}
      </div>

      <div style={{ display: 'flex', gap: '10px' }}>
        <div className={styles.itemInput} style={{ maxWidth: 166 }}>
          <span className={styles.label}>Tip:</span>
          <InputNumber
            ref={inputRef}
            value={tipValue}
            onChange={(rawValue: number | null) => {
              let value = typeof rawValue === 'number' ? rawValue : 0;

              value = Math.min(100, Math.max(0, value));
              value = Number(value.toString().slice(0, 3));

              setTipValue(value);

              setTimeout(() => {
                const inputEl: HTMLInputElement | undefined =
                  inputRef.current?.input;
                if (inputEl && inputEl.value === '0%') {
                  inputEl.setSelectionRange(1, 1); // after 0, before %
                }
              }, 0);
            }}
            min={0}
            max={100}
            formatter={(value?: number) => `${value ?? 0}%`}
            parser={(value: string | undefined): number => {
              const cleaned = (value ?? '').replace(/[^0-9]/g, '');
              const trimmed = cleaned.slice(0, 3);
              const num = Number(trimmed);
              return isNaN(num) ? 0 : Math.min(num, 100);
            }}
            onKeyDown={(e) => {
              if (
                !/[0-9]/.test(e.key) &&
                ![
                  'Backspace',
                  'Delete',
                  'ArrowLeft',
                  'ArrowRight',
                  'Tab',
                ].includes(e.key)
              ) {
                e.preventDefault();
              }

              const input = e.currentTarget as HTMLInputElement;
              const value = input.value.replace(/[^0-9]/g, '');
              const selectionStart = input.selectionStart ?? 0;
              const selectionEnd = input.selectionEnd ?? 0;
              const newLength =
                value.length -
                (selectionEnd - selectionStart) +
                (/[0-9]/.test(e.key) ? 1 : 0);

              if (newLength > 3) {
                e.preventDefault();
              }
            }}
            className={styles.input}
            style={{ color: 'white' }}
            controls={false}
          />
        </div>
        <div className={styles.itemInput}>
          <span className={styles.label}>Tip Amount:</span>
          <div
            className={styles.input}
            style={{ color: 'white', textAlign: 'center' }}
          >
            ${tipAmount.toFixed(2)}
          </div>
        </div>
      </div>

      <div className={styles.total}>
        <span>TOTAL:</span>
        <div className={styles.totalValue}>${totalAmount.toFixed(2)}</div>
      </div>
    </div>
  );
}
